import api from '../api'

export const createHrDocument = async (data: { title: string; version: string; file: File }) => {
  const formData = new FormData()
  formData.append('title', data.title)
  formData.append('version', data.version)
  formData.append('file', data.file)
  formData.append('type', 'generic')
  const response = await api.post('/documents/hr/', formData)
  return response.data
}

export const createTemplateDocument = async (data: { title: string; version: string; file: File; country?: string }) => {
  const formData = new FormData()
  formData.append('title', data.title)
  formData.append('version', data.version)
  formData.append('file', data.file)
  formData.append('type', 'generic')
  if (data.country) {
    formData.append('countries', data.country)
  }
  const response = await api.post('/documents/templates/', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    transformRequest: [
      (data) => {
        return data
      },
    ],
  })
  return response.data
}

export const createDocument = async (data: { title: string; version: string; file: File; recipient: string; status: string }) => {
  const formData = new FormData()
  formData.append('title', data.title)
  formData.append('version', data.version)
  formData.append('file', data.file)
  formData.append('status', data.status)
  formData.append('recipient', data.recipient)
  const response = await api.post('/documents/', formData)
  return response.data
}

export const createSignedDocument = async (data: { file: File; id: string }) => {
  const formData = new FormData()
  formData.append('file', data.file)
  const response = await api.post(`/documents/${data.id}/sign/`, formData)
  return response.data
}

// Digital signature acknowledgement (no file upload)
export const signDocumentDigitally = async (id: string) => {
  const response = await api.post(`/documents/${id}/sign_digital/`)
  return response.data
}

export const createDocumentFromTemplate = async (data: {
  template: string
  title: string
  signing_required: boolean
  digital_signing_required: boolean
  recipient_type: 'user' | 'all' | 'job_profile' | 'team'
  recipients?: string[]
  recipient_team?: string
  recipient_job_profile?: string
  countries?: string[]
}) => {
  const response = await api.post('/documents/', data)
  return response.data
}

export const getDocuments = async (data: {
  doc_type?: string
  page: number
  page_size: number
  search: string
  type?: string
  recipient?: string
  fields?: string
  minimal?: boolean
  filters?: { [key: string]: string[] }
}) => {
  const params: Record<string, any> = {
    page: data.page,
    page_size: data.page_size,
    search: data.search,
    type: data.type,
    recipient: data.recipient,
    // default expand for caller that need nested author/recipient
    expand: 'author,recipient',
  }

  // If caller requests minimal projection, provide a compact fields list
  if (data.minimal && !data.fields) {
    params.fields = 'id,title,type,status,file,signed_file,created_at,author.first_name,author.last_name,author.profile_picture'
  }

  // Add fields parameter if provided to limit response data
  if (data.fields) {
    params.fields = data.fields
  }
  
  
  if (data.filters) {
    Object.entries(data.filters).forEach(([key, values]) => {
      if (!values || values.length === 0) return
      
      const lowerKey = key.charAt(0).toLowerCase() + key.slice(1)
      
      if (key === 'countries' && values.includes('all')) {
        return
      }
      
      if (values.length > 1) {
        params[`${lowerKey}__in`] = values.join(',')
      } else if (values.length === 1) {
        params[lowerKey] = values[0]
      }
    })
  }
  
  
  const res = await api.get(
    `/documents/${data.doc_type && data.doc_type !== 'documents' ? data.doc_type : ''}`, 
    { params }
  )
  return res.data
}

export const deleteDocument = async (documentId: string) => {
  const response = await api.delete(`/documents/${documentId}/`)
  return response.data
}

export const deleteHrDocument = async (documentId: string) => {
  const response = await api.delete(`/documents/hr/${documentId}/`)
  return response.data
}

export const deleteTemplateDocument = async (documentId: string) => {
  const response = await api.delete(`/documents/templates/${documentId}/`)
  return response.data
}

export const updateTemplateDocument = async (id: string, data: { 
  title: string; 
  type: string; 
  file?: File;
  premium?: boolean;
}) => {
  // If there's a new file, use FormData
  if (data.file) {
    const formData = new FormData()
    formData.append('title', data.title)
    formData.append('type', data.type)
    formData.append('file', data.file)
    if (data.premium !== undefined) {
      formData.append('premium', data.premium.toString())
    }
    const response = await api.put(`/documents/templates/${id}/`, formData)
    return response.data
  } 
  // Otherwise use JSON
  else {
    const response = await api.put(`/documents/templates/${id}/`, data)
    return response.data
  }
}

export const patchTemplateDocument = async (id: string, data: { 
  title?: string; 
  type?: string; 
  file?: File;
  premium?: boolean;
}) => {
  // If there's a file in the patch, use FormData
  if (data.file) {
    const formData = new FormData()
    if (data.title) formData.append('title', data.title)
    if (data.type) formData.append('type', data.type)
    formData.append('file', data.file)
    if (data.premium !== undefined) {
      formData.append('premium', data.premium.toString())
    }
    const response = await api.patch(`/documents/templates/${id}/`, formData)
    return response.data
  } 
  // Otherwise use JSON
  else {
    const response = await api.patch(`/documents/templates/${id}/`, data)
    return response.data
  }
}

export const fetchTemplate = async (id: string, params?: { fields?: string }) => {
  const response = await api.get(`/documents/templates/${id}/`, { params })
  return response.data
}

export const fetchDocument = async (id: string, params?: { fields?: string }) => {
  const response = await api.get(`/documents/${id}/`, { params })
  return response.data
}

export const updateDocument = async (
  id: string,
  data: { title?: string; type?: string; file?: File; status?: string; recipient?: string; countries?: string[] },
) => {
  const url = `/documents/${id}/`

  if (data.file) {
    const formData = new FormData()
    if (data.title !== undefined) formData.append('title', data.title)
    if (data.type !== undefined) formData.append('type', data.type)
    if (data.status !== undefined) formData.append('status', data.status)
    if (data.recipient !== undefined) formData.append('recipient', data.recipient)
    if (data.countries !== undefined) formData.append('countries', JSON.stringify(data.countries))
    formData.append('file', data.file as File)
    
    const res = await api.put(url, formData)
    return res.data
  }

  const current = await fetchDocument(id)
  const fullPayload = {
    title: data.title !== undefined ? data.title : current.title,
    type: data.type !== undefined ? data.type : current.type,
    status: data.status !== undefined ? data.status : current.status,
    recipient: data.recipient !== undefined ? data.recipient : current.recipient?.id,
    countries: data.countries !== undefined ? data.countries : current.countries,
  }

  const res = await api.put(url, fullPayload)
  return res.data
}

export const fetchRecipients = async () => {
  const response = await api.get(`/documents/recipients/`)
  return response.data
}

// Template Signature Tracking
export const getTemplateSignatureOverview = async (templateId: string, version?: string) => {
  const params = version ? { version } : {}
  const response = await api.get(`/documents/templates/${templateId}/signature_overview/`, { params })
  return response.data
}

export const getTemplateVersions = async (templateId: string) => {
  const response = await api.get(`/documents/templates/${templateId}/versions/`)
  return response.data
}

export const getAllTemplatesStatistics = async (
  {
    page = 1,
    page_size = 10,
    search = '',
    has_pending = false,
  }: {
    page?: number
    page_size?: number
    search?: string
    has_pending?: boolean
  } = {},
) => {
  const params: Record<string, any> = {
    page,
    page_size,
  }

  if (search) params.search = search
  if (has_pending) params.has_pending = 'true'

  const response = await api.get(`/documents/templates/statistics/`, { params })
  return response.data
}
